import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, MapPin, FileText, Wallet, Building2, Hammer,
  HardHat, KeyRound, ClipboardCheck, ShieldCheck, Sun, CloudRain,
  CheckCircle2, Phone, Calendar
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';

const t = (lang, fr, en) => (lang === 'fr' ? fr : en);

const ConstructionMaisonPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const heroImg =
    'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1920&q=70';

  // Steps timeline
  const steps = [
    {
      n: '01',
      Icon: Wallet,
      title: t(language, 'Préparation & budget', 'Preparation & budget'),
      text: t(
        language,
        "Définition du programme, estimation du coût total (terrain, travaux, taxes, raccordements, imprévus 10–15 %), recherche de financement bancaire ou apport personnel, ouverture d'un compte projet.",
        "Defining the brief, estimating total cost (land, works, taxes, utility hook-ups, 10–15 % contingency), securing bank financing or own funds, opening a dedicated project account."
      ),
    },
    {
      n: '02',
      Icon: MapPin,
      title: t(language, "Choix & acquisition du terrain", 'Land selection & acquisition'),
      text: t(
        language,
        "Identification de la parcelle à Kinshasa (Gombe, Lemba, Mont-Ngafula, Kintambo…), vérification de la situation foncière à la Conservation des Titres Immobiliers, étude de sol et levé topographique avant l'achat.",
        'Plot identification in Kinshasa (Gombe, Lemba, Mont-Ngafula, Kintambo…), verification of legal status at the Land Titles Conservancy, soil testing and topographic survey before purchase.'
      ),
    },
    {
      n: '03',
      Icon: FileText,
      title: t(language, 'Régularisation foncière', 'Land regularisation'),
      text: t(
        language,
        "Obtention du Certificat d'enregistrement, du contrat de cession ou de location, mutation auprès du Cadastre et inscription définitive du titre au nom du propriétaire.",
        'Obtaining the Registration Certificate, transfer or lease deed, change of ownership at the Cadastre and final title registration in the owner\u2019s name.'
      ),
    },
    {
      n: '04',
      Icon: ClipboardCheck,
      title: t(language, 'Études & plans', 'Studies & drawings'),
      text: t(
        language,
        "Avant-projet sommaire, plans d'architecture, plans de structure (béton armé, charpente), plans techniques (électricité, plomberie, climatisation) et descriptif détaillé des matériaux.",
        'Preliminary design, architectural drawings, structural drawings (reinforced concrete, framing), MEP drawings (electrical, plumbing, HVAC) and detailed material specifications.'
      ),
    },
    {
      n: '05',
      Icon: ShieldCheck,
      title: t(language, "Permis de bâtir", 'Building permit'),
      text: t(
        language,
        "Dépôt du dossier auprès du service d'urbanisme communal et de l'Hôtel de Ville de Kinshasa, paiement des taxes (DGRK) et obtention de l'autorisation de bâtir avant tout démarrage.",
        'Filing of the application with the municipal urban planning service and Kinshasa City Hall, payment of taxes (DGRK) and obtaining the building authorisation before any work starts.'
      ),
    },
    {
      n: '06',
      Icon: HardHat,
      title: t(language, 'Fondations & gros œuvre', 'Foundations & shell'),
      text: t(
        language,
        "Implantation, terrassement, fondations adaptées au sol kinois (souvent sablo-argileux), élévation des murs porteurs, dalles, charpente et couverture étanche pour traverser la saison des pluies.",
        'Setting out, earthworks, foundations adapted to the typical sandy-clay soil of Kinshasa, load-bearing walls, slabs, framing and watertight roofing to withstand the rainy season.'
      ),
    },
    {
      n: '07',
      Icon: Hammer,
      title: t(language, 'Second œuvre', 'Second fix'),
      text: t(
        language,
        "Cloisons, enduits, électricité conforme aux standards SNEL, plomberie et raccordement REGIDESO, menuiseries aluminium/bois, climatisation, peinture intérieure et extérieure.",
        'Partitions, plaster, SNEL-compliant electrics, plumbing with REGIDESO connection, aluminium/wood joinery, air conditioning, interior and exterior paint.'
      ),
    },
    {
      n: '08',
      Icon: KeyRound,
      title: t(language, 'Réception & remise des clés', 'Handover & keys'),
      text: t(
        language,
        "Visite de pré-réception, levée des réserves, certificat de conformité, remise du dossier des ouvrages exécutés (DOE) et garanties (parfait achèvement, biennale, décennale).",
        'Pre-handover visit, snagging, conformity certificate, handing over of as-built file (DOE) and warranties (defects, biennial, decennial).'
      ),
    },
  ];

  const prepBlocks = [
    {
      Icon: Wallet,
      title: t(language, 'Finances & budget', 'Finances & budget'),
      points: [
        t(language, "Définir un budget global TTC (TVA 16 % en RDC).", 'Define a total budget incl. taxes (16 % VAT in DRC).'),
        t(language, "Prévoir 10 à 15 % de réserve pour imprévus.", 'Plan a 10–15 % contingency for unforeseen costs.'),
        t(language, "Comparer apport personnel vs. crédit immobilier (Rawbank, Equity, TMB, FBN Bank).", 'Compare own funds vs. mortgage (Rawbank, Equity, TMB, FBN Bank).'),
        t(language, "Anticiper les frais notariaux, taxes communales et raccordements.", 'Anticipate notary fees, municipal taxes and utility hook-ups.'),
      ],
    },
    {
      Icon: MapPin,
      title: t(language, 'Choix du terrain', 'Choosing the land'),
      points: [
        t(language, "Vérifier le statut foncier (titre, contrat, antériorité de propriété).", 'Verify the legal status (title, contract, prior ownership).'),
        t(language, "Étude de sol et levé topographique systématiques.", 'Systematic soil testing and topographic survey.'),
        t(language, "Évaluer l'accès, l'altitude (zones inondables) et le voisinage.", 'Assess access, elevation (flood-prone areas) and neighbourhood.'),
        t(language, "Confirmer la viabilisation : eau (REGIDESO), électricité (SNEL), assainissement.", 'Confirm utility availability: water (REGIDESO), electricity (SNEL), sanitation.'),
      ],
    },
    {
      Icon: FileText,
      title: t(language, 'Démarches administratives', 'Administrative procedures'),
      points: [
        t(language, "Constituer le dossier de demande de permis de bâtir.", 'Compile the building permit application file.'),
        t(language, "Obtenir l'autorisation de bâtir auprès de la commune et de l'Hôtel de Ville.", 'Obtain the building authorisation from the commune and City Hall.'),
        t(language, "Déclarer les ouvrages auprès du Cadastre et de la Conservation des Titres.", 'Declare works with the Cadastre and the Titles Conservancy.'),
        t(language, "Régulariser les taxes locales (DGRK) et nationales (DGI).", 'Settle local (DGRK) and national (DGI) taxes.'),
      ],
    },
    {
      Icon: Building2,
      title: t(language, 'Choix du constructeur', 'Choosing the contractor'),
      points: [
        t(language, "Vérifier l'enregistrement RCCM, NIF et l'expérience BTP locale.", 'Check RCCM and NIF registration and local BTP experience.'),
        t(language, "Demander des références de chantiers livrés à Kinshasa.", 'Request references of projects delivered in Kinshasa.'),
        t(language, "Exiger un contrat écrit, un planning, un devis détaillé et des garanties.", 'Require a written contract, schedule, detailed quote and warranties.'),
        t(language, "Privilégier un interlocuteur unique du début à la livraison.", 'Prefer a single point of contact from start to handover.'),
      ],
    },
  ];

  // RDC specifics
  const rdcSpecifics = [
    {
      Icon: CloudRain,
      title: t(language, 'Climat & saisonnalité', 'Climate & seasonality'),
      text: t(
        language,
        "Kinshasa connaît une saison des pluies d'octobre à mai et une saison sèche de juin à septembre. Le planning de chantier doit caler les fondations et la mise hors d'eau avant les pluies, et réserver les finitions à la saison sèche.",
        'Kinshasa has a rainy season from October to May and a dry season from June to September. The schedule should plan foundations and roof close-up before the rains, and reserve finishes for the dry season.'
      ),
    },
    {
      Icon: HardHat,
      title: t(language, 'Matériaux locaux', 'Local materials'),
      text: t(
        language,
        "Ciment (CILU, Cimenko, PPC Barnet), fer à béton local et importé, briques cuites, parpaings creux, sable de Maluku, gravier de Kasangulu, bois Wenge/Sapelli. Privilégier les filières contrôlées pour la résistance et la durabilité.",
        'Cement (CILU, Cimenko, PPC Barnet), local and imported rebar, fired bricks, hollow blocks, Maluku sand, Kasangulu gravel, Wenge/Sapelli wood. Use quality-controlled supply chains for strength and durability.'
      ),
    },
    {
      Icon: ShieldCheck,
      title: t(language, 'Sécurité & assurances', 'Safety & insurance'),
      text: t(
        language,
        "Mise en place du plan de prévention HSE, EPI obligatoires, déclaration auprès de l'INPP et CNSS pour la main-d'œuvre, souscription d'une assurance tous risques chantier (TRC).",
        'HSE prevention plan, mandatory PPE, INPP and CNSS declarations for the workforce, all-risk site insurance (TRC).'
      ),
    },
    {
      Icon: Sun,
      title: t(language, 'Énergie & autonomie', 'Energy & autonomy'),
      text: t(
        language,
        "Anticiper les coupures du réseau SNEL : groupe électrogène silencieux, onduleurs, et kit solaire photovoltaïque (3 à 10 kWc) pour les usages essentiels et l'éclairage.",
        'Plan for SNEL grid outages: silent generator, inverters, and a 3–10 kWp solar PV kit for essential loads and lighting.'
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* HERO */}
      <main className="pt-[240px]" data-testid="construction-house-page">
        <section
          className="relative h-[360px] md:h-[460px] overflow-hidden"
          data-testid="house-hero"
        >
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImg})` }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <button
              onClick={() => navigate('/')}
              className="self-start inline-flex items-center gap-2 text-white/85 hover:text-orange-400 mb-6 transition-colors"
              data-testid="house-back-btn"
            >
              <ArrowLeft size={18} />
              <span className="font-semibold">{t(language, "Retour à l'accueil", 'Back to home')}</span>
            </button>
            <h1 className="text-white text-3xl sm:text-4xl md:text-6xl font-bold leading-tight max-w-3xl">
              {t(
                language,
                "Construire votre maison ou votre bâtiment à Kinshasa",
                'Build your house or building in Kinshasa'
              )}
            </h1>
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {t(
                language,
                "De la préparation client à la remise des clés : les étapes clés d'un projet BTP réussi en République Démocratique du Congo.",
                'From client preparation to key handover: the key steps of a successful BTP project in the Democratic Republic of Congo.'
              )}
            </p>
          </div>
        </section>

        {/* INTRO */}
        <section className="py-16 md:py-20" data-testid="house-intro">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-6" style={{ color: '#702963' }}>
              {t(
                language,
                "Un projet, deux univers : maison individuelle & bâtiment professionnel",
                'One project, two worlds: individual house & commercial building'
              )}
            </h2>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              {t(
                language,
                "Que vous souhaitiez bâtir votre résidence familiale dans une commune de Kinshasa ou édifier un immeuble de bureaux, un entrepôt logistique ou un bâtiment commercial, la démarche s'appuie sur les mêmes piliers : préparation rigoureuse, conformité administrative, exécution maîtrisée et accompagnement continu. GLOBAL CONSTRUCTION & INGÉNIERIE SARL vous guide à chaque étape, en s'appuyant sur les spécificités du marché BTP en République Démocratique du Congo.",
                "Whether you wish to build your family home in a Kinshasa commune or erect an office building, a logistics warehouse or a commercial structure, the process relies on the same pillars: rigorous preparation, regulatory compliance, controlled execution and continuous support. GLOBAL CONSTRUCTION & INGÉNIERIE SARL guides you at every step, leveraging the specifics of the BTP market in the Democratic Republic of Congo."
              )}
            </p>
          </div>
        </section>

        {/* CLIENT PREPARATION */}
        <section className="py-16 md:py-20 bg-gray-50" data-testid="house-prep">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Préparation client : 4 piliers à sécuriser", 'Client preparation: 4 pillars to secure')}
              </h2>
              <div className="mx-auto h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {prepBlocks.map((b, idx) => {
                const Icon = b.Icon;
                return (
                  <div
                    key={idx}
                    className="bg-white border border-gray-200 rounded-sm p-7 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300"
                    data-testid={`prep-block-${idx}`}
                  >
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-14 h-14 flex items-center justify-center bg-orange-50 rounded-full text-orange-600 flex-shrink-0">
                        <Icon className="w-7 h-7" strokeWidth={2} />
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900">{b.title}</h3>
                    </div>
                    <ul className="space-y-3">
                      {b.points.map((p, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-700 text-sm md:text-base leading-relaxed">
                          <CheckCircle2 className="w-5 h-5 text-[#702963] flex-shrink-0 mt-0.5" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* STEPS TIMELINE */}
        <section className="py-16 md:py-24" data-testid="house-steps">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, "Les 8 étapes d'un projet de construction", 'The 8 steps of a construction project')}
              </h2>
              <p className="text-gray-600 text-base md:text-lg">
                {t(
                  language,
                  "De la première esquisse à la remise des clés.",
                  'From the first sketch to the keys handover.'
                )}
              </p>
              <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>

            <div className="relative">
              {/* vertical line */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 -translate-x-1/2" />
              <div className="space-y-10 md:space-y-16">
                {steps.map((s, i) => {
                  const Icon = s.Icon;
                  const left = i % 2 === 0;
                  return (
                    <div key={s.n} className="md:grid md:grid-cols-2 md:gap-12 items-center" data-testid={`step-${s.n}`}>
                      <div className={`${left ? 'md:order-1 md:text-right' : 'md:order-2'} mb-4 md:mb-0`}>
                        <div className={`flex items-start gap-4 ${left ? 'md:flex-row-reverse md:text-right' : ''}`}>
                          <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#702963] text-white rounded-full flex-shrink-0">
                            <Icon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2} />
                          </div>
                          <div>
                            <span className="text-orange-500 font-bold text-sm tracking-widest">
                              {t(language, 'ÉTAPE', 'STEP')} {s.n}
                            </span>
                            <h3 className="text-lg md:text-2xl font-bold text-gray-900 mt-1 mb-2">
                              {s.title}
                            </h3>
                            <p className="text-gray-700 text-sm md:text-base leading-relaxed">{s.text}</p>
                          </div>
                        </div>
                      </div>
                      <div className={`hidden md:block ${left ? 'md:order-2' : 'md:order-1'}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* RDC SPECIFICS */}
        <section className="py-16 md:py-24 bg-gray-50" data-testid="house-rdc-specifics">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: '#702963' }}>
                {t(language, 'Spécificités du BTP à Kinshasa', 'BTP specifics in Kinshasa')}
              </h2>
              <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
                {t(
                  language,
                  "Bâtir en RDC, c'est composer avec un climat tropical, un cadre foncier rigoureux et une logistique exigeante.",
                  'Building in DRC means dealing with a tropical climate, a rigorous land framework and demanding logistics.'
                )}
              </p>
              <div className="mx-auto mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {rdcSpecifics.map((r, i) => {
                const Icon = r.Icon;
                return (
                  <div
                    key={i}
                    className="bg-white p-6 md:p-7 border-t-4 border-[#702963] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    data-testid={`rdc-card-${i}`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-full text-orange-600 mb-4">
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">{r.title}</h3>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">{r.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* TIMELINE INDICATIVE */}
        <section className="py-16 md:py-20" data-testid="house-duration">
          <div className="max-w-5xl mx-auto px-6">
            <div className="bg-[#702963] text-white p-8 md:p-12 rounded-sm shadow-2xl">
              <div className="flex items-start gap-5">
                <Calendar className="w-10 h-10 text-orange-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-3xl font-bold mb-3">
                    {t(language, 'Durée indicative', 'Indicative duration')}
                  </h3>
                  <p className="text-white/90 text-base md:text-lg leading-relaxed">
                    {t(
                      language,
                      "Pour une maison individuelle de standing à Kinshasa, comptez en moyenne 8 à 14 mois entre la signature du contrat et la remise des clés. Pour un bâtiment commercial ou un immeuble R+3 à R+5, la durée s'étend généralement de 14 à 24 mois selon la complexité, la disponibilité des matériaux et la coordination des corps d'état.",
                      'For a high-end individual house in Kinshasa, allow 8 to 14 months on average from contract signature to keys handover. For a commercial building or a 3- to 5-storey building, allow 14 to 24 months depending on complexity, material availability and trade coordination.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-white" data-testid="house-cta">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#702963' }}>
              {t(language, 'Prêt à lancer votre projet ?', 'Ready to launch your project?')}
            </h2>
            <p className="text-gray-700 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              {t(
                language,
                "Nos ingénieurs et architectes étudient gratuitement la faisabilité de votre projet et vous proposent un devis détaillé sous 7 jours ouvrés.",
                'Our engineers and architects study the feasibility of your project free of charge and provide a detailed quote within 7 business days.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/votre-projet')}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-sm transition-all duration-300 transform hover:scale-105"
                data-testid="house-cta-project-btn"
              >
                {t(language, 'Démarrer mon projet', 'Start my project')}
                <ArrowRight size={18} />
              </button>
              <a
                href="tel:+243971327318"
                className="inline-flex items-center gap-2 border-2 border-[#702963] text-[#702963] font-semibold px-8 py-3 hover:bg-[#702963] hover:text-white transition-all"
                data-testid="house-cta-call-btn"
              >
                <Phone size={18} />
                {t(language, 'Appelez-nous', 'Call us')}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ConstructionMaisonPage;
